import React, { useEffect, useRef, useState } from 'react';
import { useLiveEditor } from '../../contexts/LiveEditorContext';
import { useTranslation } from 'react-i18next';

interface NewsletterDraft {
  title: string;
  content: string;
  date: string;
  charCount: number;
}

interface NewsletterDraftsProps {
  drafts: NewsletterDraft[];
}

const NewsletterContent: React.FC<{ content: string }> = ({ content }) => {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const { appendToEditor, editedResult } = useLiveEditor();
  const { t } = useTranslation();
  const [addedParagraphs, setAddedParagraphs] = useState<string[]>([]);

  // Listen for when the editor is cleared
  useEffect(() => {
    if (!editedResult.trim()) {
      setAddedParagraphs([]);
    }
  }, [editedResult]);

  useEffect(() => {
    if (iframeRef.current) {
      const iframe = iframeRef.current;
      const iframeDocument = iframe.contentDocument || iframe.contentWindow?.document;

      if (iframeDocument) {
        iframeDocument.open();
        iframeDocument.write(`
                    <!DOCTYPE html>
                    <html>
                        <head>
                            <base target="_blank">
                            <style>
                                body {
                                    font-family: system-ui, -apple-system, sans-serif;
                                    margin: 0;
                                    padding: 0;
                                    color: inherit;
                                    min-height: 100%;
                                }
                                * {
                                    box-sizing: border-box;
                                }
                                .paragraph {
                                    position: relative;
                                    padding: 4px;
                                    border-radius: 4px;
                                    border: 1px solid transparent;
                                }
                                .paragraph:hover {
                                    border-color: #90caf9;
                                }
                                .paragraph.added {
                                    border-color: #4caf50;
                                }
                                .add-button {
                                    position: absolute;
                                    top: 4px;
                                    right: 4px;
                                    background-color: #3f51b5;
                                    color: white;
                                    border: none;
                                    border-radius: 4px;
                                    padding: 2px 8px;
                                    font-size: 12px;
                                    cursor: pointer;
                                    display: none;
                                }
                                .paragraph:hover .add-button {
                                    display: block;
                                }
                                .add-button.added::before {
                                    content: "✓";
                                    margin-right: 4px;
                                }
                            </style>
                        </head>
                        <body>${content}</body>
                    </html>
                `);
        iframeDocument.close();

        // Improved resize function with a small buffer
        const resizeIframe = () => {
          if (iframeDocument.body) {
            const height = Math.max(
              iframeDocument.documentElement.scrollHeight,
              iframeDocument.body.scrollHeight,
              iframeDocument.documentElement.offsetHeight,
              iframeDocument.body.offsetHeight
            );
            iframe.style.height = `${height + 20}px`; // Adding 20px buffer
          }
        };

        // Wrap each paragraph for hover functionality
        const paragraphs = iframeDocument.body.querySelectorAll('p, h1, h2, h3');
        paragraphs.forEach((paragraph, index) => {
          const wrapper = iframeDocument.createElement('div');
          wrapper.className = 'paragraph';
          wrapper.dataset.id = `p-${index}`;
          paragraph.parentNode?.insertBefore(wrapper, paragraph);
          wrapper.appendChild(paragraph);

          const addButton = iframeDocument.createElement('button');
          addButton.className = 'add-button';
          addButton.textContent = t('common.add_to_live_editor');
          addButton.onclick = (e) => {
            e.preventDefault();
            const paragraphText = paragraph.innerHTML;
            appendToEditor(paragraphText);
            setAddedParagraphs((prev) => [...prev, paragraphText]);
            wrapper.classList.add('added');
            addButton.classList.add('added');
          };

          if (addedParagraphs.includes(paragraph.innerHTML)) {
            wrapper.classList.add('added');
            addButton.classList.add('added');
          }

          wrapper.appendChild(addButton);
        });

        // Initial resize
        resizeIframe();

        // Handle dynamic content changes
        const resizeObserver = new ResizeObserver(resizeIframe);
        resizeObserver.observe(iframeDocument.body);

        // Handle image loads
        iframeDocument.body.querySelectorAll('img').forEach((img) => {
          img.addEventListener('load', resizeIframe);
        });

        // Cleanup
        return () => {
          resizeObserver.disconnect();
        };
      }
    }
  }, [content, t, appendToEditor, addedParagraphs]);

  return (
    <iframe
      ref={iframeRef}
      className="w-full border-none"
      title="Newsletter content"
      sandbox="allow-same-origin allow-scripts"
      scrolling="no"
      style={{ overflow: 'hidden' }}
    />
  );
};

export const NewsletterDrafts: React.FC<NewsletterDraftsProps> = ({ drafts }) => {
  const { t } = useTranslation();

  return (
    <div className="mt-8">
      <h2 className="text-2xl font-semibold mb-4 text-gray-800 dark:text-gray-100">
        {t('newsletter.generated_drafts')}
      </h2>
      <div className="space-y-6">
        {drafts.map((draft, index) => (
          <div
            key={index}
            className="bg-white dark:bg-gray-800 rounded-lg shadow-md overflow-hidden"
          >
            <div className="header border-b text-gray-600 p-4 text-left">
              <h3 className="font-bold">{draft.title}</h3>
            </div>
            <div className="email-meta p-4 text-sm text-gray-600 dark:text-gray-400 space-y-1 border-b">
              <div>From: Your Company Name</div>
              <div>To: Newsletter Subscribers</div>
              <div>{draft.date}</div>
            </div>
            <div className="content p-6">
              <NewsletterContent
                content={draft.content
                  .replace(/^```html\n/, '')
                  .replace(/\n```$/, '')
                  .replace(/^```$/, '')
                  .trim()}
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
